import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Play, X, MapPin } from 'lucide-react';
import { GALLERY_IMAGES, GALLERY_VIDEOS } from '../data/site';

const TABS = [
  { key: 'images', label: 'Images' },
  { key: 'videos', label: 'Videos' },
];

export default function Gallery() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'videos' ? 'videos' : 'images';
  const [lightbox, setLightbox] = useState(null);

  return (
    <main data-testid="gallery-page" className="pt-36 pb-32 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">ON THE GROUND — SITE GALLERY</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading font-extrabold tracking-tighter text-5xl sm:text-7xl text-ink"
          >
            Gallery
          </motion.h1>
          <div className="flex gap-2" data-testid="gallery-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setParams(t.key === 'images' ? {} : { tab: 'videos' })}
                data-testid={`gallery-tab-${t.key}`}
                className={`text-[11px] tracking-[0.18em] font-medium px-6 py-3 border transition-colors duration-300 ${
                  tab === t.key ? 'bg-moss text-sand border-moss' : 'border-clay text-ink/60 hover:border-terra hover:text-terra'
                }`}
              >
                {t.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {tab === 'images' ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="gallery-masonry">
            {GALLERY_IMAGES.map((img, i) => (
              <motion.figure
                key={img.src}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group relative mb-5 break-inside-avoid overflow-hidden bg-stone cursor-pointer"
                onClick={() => setLightbox(img)}
                data-testid={`gallery-item-${i}`}
              >
                <img src={img.src} alt={img.caption} loading="lazy"
                  className="w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-bark/80 to-transparent px-5 pt-12 pb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                  <span className="text-xs text-sand flex items-center gap-2"><MapPin size={12} className="text-terra" /> {img.caption}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        ) : (
          <div className="max-w-4xl space-y-6" data-testid="gallery-videos">
            {GALLERY_VIDEOS.map((v, i) => (
              <motion.div
                key={v.src}
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: i * 0.1 }}
                className="border border-clay bg-stone"
              >
                <video
                  controls preload="none" poster={v.poster}
                  className="w-full aspect-video object-cover bg-bark"
                  data-testid={`gallery-video-player-${i}`}
                >
                  <source src={v.src} type="video/mp4" />
                </video>
                <p className="px-6 py-5 text-sm text-ink/60 flex items-center gap-2">
                  <Play size={14} className="text-terra" /> {v.caption}
                </p>
              </motion.div>
            ))}
            <p className="text-sm text-ink/50 max-w-md leading-relaxed">
              More walkthroughs and drone films are being edited. Follow us for updates — or book a site visit and see it yourself.
            </p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-bark/90 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-sand/70 hover:text-terra transition-colors duration-200" aria-label="Close" data-testid="lightbox-close-btn">
              <X size={26} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[78vh] object-contain" />
              <figcaption className="mt-4 text-center text-sm text-sand/70">{lightbox.caption}</figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
